/** @format */

import { ActivatedRoute, Router } from '@angular/router';
import {
  AlertMessageModule,
  DSProductRecommendationsComponent,
  DrawerProductRecommendationsComponent,
} from '@shared/components';
import { BreakPoint, MyAccountErrorLogKeys } from '@shared/data-access/enums';
import {
  Chain,
  KillSwitches,
  PlpPromoMessages,
  ProductAvailability,
  ProductAvailabilityCollection,
  PromoDetailsMap,
} from '@shared/data-access/interfaces';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  NgZone,
  OnInit,
  ViewChild,
  computed,
  effect,
  inject,
  input,
  model,
  signal,
} from '@angular/core';
import {
  CookieService,
  DeviceService,
  EmitEvent,
  EventBusService,
  Events,
  MyAccountAnalyticsService,
  ProductCatalogService,
  Store,
  StoreService,
  UtilityService,
  WINDOW,
  WINDOW_PROVIDERS,
} from '@shared/data-access/services';
import { EMPTY, Observable, catchError, fromEvent, of } from 'rxjs';
import {
  FavoriteListState,
  FavoriteProductCard,
  FavoriteProductState,
  IOrchestratedListDetails,
} from '@shared/favorites/data-access/interfaces';
import { HmfButtonComponent, HmfDrawerComponent } from '@dsg-tech/homefield-angular';
import { concatMap, map, mergeMap, startWith, switchMap, take, tap } from 'rxjs/operators';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';

import { CommonModule } from '@angular/common';
import { DiscountEligibilityService } from '@shared/data-access/product';
import { FavoritesIconComponent } from '../favorites-icon/favorites-icon.component';
import { FavoritesLocationEnum } from '@shared/favorites/data-access/enums';
import { HomefieldIconLibraryModule } from '@dsg-tech/homefield-angular-icons';
import { HomefieldProductCardModule } from '@ecomm-patterns-components';
import { NonStorePaginationComponent } from '../pagination/non-store-pagination.component';
import { WishListService } from '@shared/favorites/data-access/services';

const PAGE_SIZE = 24;
const LIST_PAGE_ITEMS = 12;

@Component({
  selector: 'shared-favorites-components-valid',
  templateUrl: './valid-favorites.component.html',
  styleUrl: './valid-favorites.component.scss',
  providers: [WINDOW_PROVIDERS],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AlertMessageModule,
    CommonModule,
    DrawerProductRecommendationsComponent,
    DSProductRecommendationsComponent,
    FavoritesIconComponent,
    HomefieldIconLibraryModule,
    HomefieldProductCardModule,
    NonStorePaginationComponent,
    HmfButtonComponent,
    HmfDrawerComponent,
  ],
})
export class ValidFavoritesComponent implements OnInit {
  protected readonly FavoritesLocationEnum = FavoritesLocationEnum;

  private readonly myAccountAnalyticsService = inject(MyAccountAnalyticsService);
  private readonly utilityService = inject(UtilityService);
  private readonly store = inject(Store);

  private readonly productCatalogService = inject(ProductCatalogService);
  private readonly storeService = inject(StoreService);
  private readonly cookieService = inject(CookieService);
  private readonly wishlistService = inject(WishListService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly eventBusService = inject(EventBusService);
  private readonly zone = inject(NgZone);
  private readonly deviceService = inject(DeviceService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly window = inject(WINDOW);
  private readonly discountEligibilityService = inject(DiscountEligibilityService);

  private readonly currentProductsState = signal<FavoriteProductState[]>([]);
  private readonly initialLoadReady = signal(false);

  protected readonly listSignal = signal<FavoriteListState | IOrchestratedListDetails | null>(null);

  protected readonly currentPageSignal = signal(1);
  protected readonly triggerRefresh = signal(0);

  private initialLoadTriggered = false;

  public chain = inject(Chain);

  private readonly killswitchValues = toSignal<KillSwitches | undefined>(
    this.store.selectDistinctKey('ENVIRONMENT_KILLSWITCH_VALUES'),
    { initialValue: undefined },
  );

  private readonly currentSavedStore = toSignal(this.store.selectDistinctKey('CUST_STORE'), {
    initialValue: undefined,
  });

  private readonly selectedStoreIdState = computed(() => {
    const currentSavedStore = this.currentSavedStore();

    if (!currentSavedStore) {
      return '';
    }

    return this.storeService.findStoreData(currentSavedStore, 'storenumber') || '';
  });

  private readonly dataRefreshTrigger$ = computed(() => ({
    page: this.currentPageSignal(),
    refresh: this.triggerRefresh(),
  }));

  private readonly promoMessagesState = toSignal(
    toObservable(this.currentProductsState).pipe(
      switchMap((products) =>
        this.discountEligibilityService.getPromoList$(products).pipe(
          map(
            (promoList) => this.discountEligibilityService.generatePromoMessages(promoList) || {},
          ),
          catchError((error) => {
            this.myAccountAnalyticsService.generateLogError(
              MyAccountErrorLogKeys.FAVORITES,
              'Error retrieving promo messages for favorites list',
              error,
            );
            return of({});
          }),
        ),
      ),
    ),
    { initialValue: {} as PlpPromoMessages | PromoDetailsMap },
  );

  protected readonly listData$ = toSignal(
    toObservable(this.dataRefreshTrigger$).pipe(
      switchMap(({ page, refresh }) => {
        // Return initial list for page 1 or when no pagination needed
        if (refresh === 0 && (!this.list()?.list || page === 1)) {
          return of(this.listSignal());
        }
        // For pages > 1 or refresh, fetch new data but start with current data
        return this.createProductDetailsObservable(page).pipe(
          startWith(this.listSignal()), // Retain current data while loading

          tap((data) => {
            // Set loading to false when new data arrives (different from current)
            if (data && data !== this.list()) {
              this.listSignal.set(data); // Update the main signal with new data
              this.favoriteIconIsDisabled.set(false);
            }
          }),
        );
      }),
    ),
    { initialValue: null },
  );

  list = model.required<FavoriteListState>();
  isPublicList = input(false);
  isRecommendedList = input(false);
  isSharedRoute = input(false);
  totalCount = 0;
  colors: Map<string, string[]> = new Map<string, string[]>();
  productAttributes = new Map();
  currentPage = 1;
  ecodeMessagingMap = signal<{ [key: string]: ProductAvailability }>({});
  sidePanelOpen = false;
  isMobile = false;
  listReferrer = 'myaccount';
  panelData = {
    productTitle: '',
    attributes: [],
  };

  favoriteIconIsDisabled = signal(false);

  // killswitches
  useNewPricingService = false;
  dataDrivenListEnabled = false;
  showDSRecommendationsFP = false;
  oosListEnabled = false;
  visualUpliftEnabled = false;
  showLoadMore = false;
  viewMoreProductsIteration = 1;

  @ViewChild(DrawerProductRecommendationsComponent) drawerComponent:
    | DrawerProductRecommendationsComponent
    | undefined;

  @ViewChild(HmfDrawerComponent) hmfDrawerComponent: HmfDrawerComponent | undefined;

  constructor() {
    effect(() => {
      const killswitch = this.killswitchValues();

      if (!killswitch) {
        return;
      }

      this.useNewPricingService = this.utilityService.checkKillSwitch(
        'useNewPricingService',
        killswitch,
        this.chain.chainIdentifierAbbr,
      );
      this.dataDrivenListEnabled = this.utilityService.checkKillSwitch(
        'dataDrivenListEnabled',
        killswitch,
        this.chain.chainIdentifierAbbr,
      );
      this.showDSRecommendationsFP = this.utilityService.checkKillSwitch(
        'showDSRecommendationsFP',
        killswitch,
        this.chain.chainIdentifierAbbr,
      );
      this.oosListEnabled = this.utilityService.checkKillSwitch(
        'oosListEnabled',
        killswitch,
        this.chain.chainIdentifierAbbr,
      );
      this.visualUpliftEnabled = this.utilityService.checkKillSwitch(
        'visualUpliftEnabled',
        killswitch,
        this.chain.chainIdentifierAbbr,
      );

      if (!this.initialLoadReady() || this.initialLoadTriggered) {
        return;
      }

      this.initialLoadTriggered = true;

      this.checkKillswitchAndGetProductData();
    });

    effect(() => {
      if (this.list()) {
        this.listSignal.set(this.list());
      }
    });
  }

  get currentProducts(): FavoriteProductState[] {
    return this.currentProductsState();
  }

  set currentProducts(products: FavoriteProductState[]) {
    this.currentProductsState.set([...(products || [])]);
  }

  get selectedStoreId(): string {
    return this.selectedStoreIdState();
  }

  get promoMessages(): PlpPromoMessages | PromoDetailsMap {
    return this.promoMessagesState();
  }

  get ecodesList(): string[] {
    return this.currentProducts
      .map((product) => product.ecode)
      .filter((ecode): ecode is string => Boolean(ecode));
  }

  ngOnInit(): void {
    this.listSignal.set(this.list());
    this.currentPage = Number.parseInt(this.activatedRoute.snapshot.queryParams?.page) || 1;
    this.totalCount =
      this.listSignal()._metaData?.totalCount || this.listSignal().list.productCount || 0;
    this.currentProducts = this.listSignal().list.products || [];
    this.currentPageSignal.set(this.currentPage);

    if (this.isPublicList()) {
      this.listReferrer = 'Shared Favorites';
    }

    if (this.listSignal().list.listType?.toLowerCase() === 'recommended') {
      this.listReferrer = 'DataScienceRecs';
    }

    this.initialLoadReady.set(true);

    fromEvent(this.window, 'updateFavoritesStoreData')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        concatMap(() => {
          return this.wishlistService.getFavoritedEcodes().pipe(
            take(1),
            catchError(() => {
              this.myAccountAnalyticsService.generateLogError(
                MyAccountErrorLogKeys.FAVORITES,
                'Failed to retrieve favorited ecodes from default list on favorites update event.',
              );
              return EMPTY;
            }),
          );
        }),
      )
      .subscribe();

    this.onResize();
  }

  getColors(product: {
    definingAttributes?: Array<{ name?: string; title?: string; value?: string }>;
  }): string[] {
    if (product.definingAttributes !== undefined) {
      const colorAttr = product.definingAttributes.find(
        (attr) => attr.name === 'Color' || attr.title === 'Color',
      );

      if (colorAttr) {
        return colorAttr.value?.split('|') || [];
      }
    }

    return [];
  }

  toggleProductSidePanel(event: boolean, productId?: string): void {
    if (event) {
      this.myAccountAnalyticsService.triggerAnalyticsEvent('ModalOpen', {
        ModalName: 'List Item Details Modal',
      });
      this.hmfDrawerComponent?.openDrawer();
    } else {
      this.hmfDrawerComponent?.closeDrawer();
    }

    // look for product data to show on attribute panel
    if (productId) {
      const matchedProduct = this.currentProducts.find((product) => {
        return productId === product.id;
      });

      this.panelData = {
        productTitle: matchedProduct?.productAttributes?.name || '',
        attributes: this.productAttributes.get(matchedProduct?.id),
      };
    }
  }

  determinePageUpdateEvent($event: number | string): void {
    const activePage = Number($event);

    this.favoriteIconIsDisabled.set(true);
    if (
      this.dataDrivenListEnabled &&
      this.listSignal().list.listType?.toLowerCase() === 'recommended'
    ) {
      this.getProductAvailability()
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (availabilityResponse: ProductAvailabilityCollection) => {
            this.favoriteIconIsDisabled.set(false);
            this.ecodeMessagingMap.set(availabilityResponse.ecodeMessagingMap);
          },
          error: () => {
            this.favoriteIconIsDisabled.set(false);
          },
        });
    } else {
      this.getActivePageProductDetailsV2(activePage);
    }
  }

  getProductUrl(product: FavoriteProductCard, index: number): string {
    if (!product.sku) {
      if (this.listSignal().list.listType?.toLowerCase() === 'recommended') {
        return `${product.productAttributes.pdpSeoUrl}?recid=DS_List Contents_${this.activatedRoute.snapshot.queryParams.dsPlacementId}_${index}`;
      }
      // no sku. return default pdpSeoUrl
      return product.productAttributes.pdpSeoUrl;
    } else if (product.sku.includes('-MC-')) {
      // custom product. append design id to pdp url
      return `${product.productAttributes.pdpSeoUrl}?designId=${product.sku.substring(product.sku.indexOf('-MC-') + 4)}`;
    }

    if (this.listSignal().list.listType?.toLowerCase() === 'recommended') {
      return `${product.productAttributes.pdpSeoUrl}?sku=${product.sku}&recid=DS_List Contents_${this.activatedRoute.snapshot.queryParams.dsPlacementId}_${index}`;
    }

    // return saved sku pdp so attributes are preselected
    return `${product.productAttributes.pdpSeoUrl}?sku=${product.sku}`;
  }

  checkIsCustomOrPersonalizedProduct(product: {
    productAttributes?: { descriptiveAttributes?: Array<{ title: string }> };
  }): boolean {
    return (
      product.productAttributes?.descriptiveAttributes?.some(
        (p) => p.title.toLowerCase() === 'customizable',
      ) ||
      product.productAttributes.descriptiveAttributes?.some(
        (p) => p.title.toLowerCase() === 'personalized',
      )
    );
  }

  refreshPageAfterProductDelete() {
    if (this.currentProducts.length === 1) {
      this.currentPage = this.currentPage - 1;
      this.updatePageQueryParam();
    }

    this.determinePageUpdateEvent(this.currentPage);
  }

  getRecommendedAnalyticsData(index: number) {
    if (this.listSignal().list.listType?.toLowerCase() === 'recommended') {
      return {
        DSDisplayName: this.activatedRoute.snapshot.queryParams.dsDisplayName,
        DSDiversityLevel: '',
        DSPlacementId: this.activatedRoute.snapshot.queryParams.dsPlacementId,
        DSPosition: index,
        DSPriceRerankLevel: this.activatedRoute.snapshot.queryParams.dsPriceRerankLevel,
        DSRecsGCPProject: '',
      };
    }

    return null;
  }

  getChainAbbr(): string {
    return this.chain?.chainIdentifierAbbr ?? '';
  }

  private getActivePageProductDetailsV2(activePage: number) {
    const listIdOrToken = this.listSignal().list.id || this.listSignal().list.accessToken;

    this.wishlistService
      .getOrchestratedListDetails$(
        this.isPublicList(),
        listIdOrToken,
        Math.abs(PAGE_SIZE * (activePage - 1)),
        PAGE_SIZE,
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((favorites: IOrchestratedListDetails) => {
          // favorites is empty. Throw error to not run Product Details APIs and trigger empty favorites.
          if (!favorites.list.products.length) {
            throw new Error('EmptyList');
          }
          return favorites;
        }),
        tap((favorites: IOrchestratedListDetails) => {
          this.currentProducts = favorites.list.products;
          this.setProductAttributes();
          this.totalCount = favorites.list.productCount;
        }),
        mergeMap(() => {
          return this.getProductAvailability();
        }),
      )
      .subscribe({
        next: (availabilityResponse: ProductAvailabilityCollection) => {
          this.favoriteIconIsDisabled.set(false);
          this.ecodeMessagingMap.set(availabilityResponse.ecodeMessagingMap);
        },
        error: (err: Error) => {
          this.favoriteIconIsDisabled.set(false);
          if (err.message === 'EmptyList') {
            this.eventBusService.emit(new EmitEvent(Events.EmptyFavorites));
          }

          this.myAccountAnalyticsService.generateLogError(
            MyAccountErrorLogKeys.FAVORITES,
            `Error getting orchestrated list: ${listIdOrToken}`,
            err,
          );
        },
      });
  }

  private handlePromotionsRetrievalError(error): Observable<null> {
    // Build and send error to analytics
    this.myAccountAnalyticsService.setErrorId('DCSG_NGX_MYACCOUNT_GETSKUPROMO_ERROR');
    this.myAccountAnalyticsService.setErrorMessage(`MYACCOUNT:${error.message}`);
    this.myAccountAnalyticsService.triggerAnalyticsEvent('DCSG_NGX_MYACCOUNT_GETSKUPROMO_ERROR', {
      ErrorMessage: `MYACCOUNT:${error.message}`,
    });

    return of(null);
  }

  private getProductAvailability(): Observable<ProductAvailabilityCollection> {
    const zipCode = this.cookieService.get('deliveryZip') || this.cookieService.get('whereabouts');
    const selectedStoreId = this.selectedStoreId;
    const favoritesPartNumbers = this.currentProducts
      .map((product) => product.ecode)
      .filter((ecode): ecode is string => Boolean(ecode));

    return this.productCatalogService
      .getFavoritesAvailabilityData(favoritesPartNumbers, true, zipCode, selectedStoreId)
      .pipe(
        map((availabilityData: ProductAvailabilityCollection) => {
          // force unavailable messaging for products with broken productDetails
          availabilityData.ecodeMessagingMap['UNAVAILABLE'] = {
            inStoreInventoryMessage: 'OutOfStock',
            onlineInventoryMessage: 'OutOfStock',
          };
          return availabilityData;
        }),
      );
  }

  private checkKillswitchAndGetProductData() {
    this.setProductAttributes();
    this.getProductDataBySku()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError((error) => {
          this.handlePromotionsRetrievalError(error);
          return of(null);
        }),
        concatMap(() => {
          if (this.isRecommendedList() && this.dataDrivenListEnabled) {
            return this.wishlistService.getFavoritedEcodes();
          }
          return of(null);
        }),
        tap(() => this.showViewMore()),
      )
      .subscribe({
        error: (error) => {
          this.myAccountAnalyticsService.generateLogError(
            MyAccountErrorLogKeys.MYACCOUNT_RECOMMENDED_LIST,
            'Failed to retrieve favorited ecodes from default list.',
            error,
          );
        },
      });
  }

  private getProductDataBySku() {
    return this.getProductAvailability().pipe(
      tap((availabilityResponse: ProductAvailabilityCollection) => {
        this.ecodeMessagingMap.set(availabilityResponse.ecodeMessagingMap);
      }),
    );
  }

  private updatePageQueryParam() {
    // remove query param if page is 1
    if (this.currentPage === 1 || this.currentPage === 0) {
      this.zone.run(() => {
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { page: null },
          queryParamsHandling: 'merge',
        });
      });
    } else {
      this.zone.run(() => {
        this.router.navigate([], {
          relativeTo: this.activatedRoute,
          queryParams: { page: `${this.currentPage}` },
          queryParamsHandling: 'merge',
        });
      });
    }
  }

  private setProductAttributes() {
    this.currentProducts.forEach((product) => {
      if (product.sku && product.productAttributes) {
        const definingAttributes = product.productAttributes.definingAttributes || [];
        const customAttributes = product.productAttributes.customizerAttribute || [];
        const productAttributes = (product.productAttributes.productAttribute ||
          []) as typeof definingAttributes;
        const personalizedAttributes = product.productAttributes.personalizedAttribute || [];

        this.productAttributes.set(
          product.id,
          definingAttributes.concat(customAttributes, productAttributes, personalizedAttributes),
        );
      }
    });
  }

  openShopSimilarDrawer(ecode: string): void {
    this.myAccountAnalyticsService.triggerAnalyticsEvent('tabbedComponentClicked', {
      value: 'ListOOS',
      ecode: `${ecode}`,
      isDefault: false,
    });
    this.drawerComponent?.openDrawer();
  }

  openSkuResolveDrawer(skuResolvePanelData: any): void {
    this.window.quickview.openProductQuickView(
      skuResolvePanelData.ecode,
      skuResolvePanelData.referrer,
      {
        template: 'saveSelection',
        saveSelectionListId: skuResolvePanelData.productListID,
        callback: () => {
          this.triggerRefresh.update((val) => val + 1);
        },
      },
    );
  }

  @HostListener('window:resize', [])
  public onResize(): void {
    const breakpoint = this.deviceService.getDeviceBreakPoint(true);
    this.isMobile = breakpoint === BreakPoint.EXTRASMALL || breakpoint === BreakPoint.SMALL;
  }

  showViewMore() {
    if (this.currentProducts.length === 0 || !this.visualUpliftEnabled) return;

    this.showLoadMore =
      this.totalCount > LIST_PAGE_ITEMS && this.totalCount - this.currentProducts.length > 0;
  }

  viewMoreProducts() {
    const listIdOrToken = this.listSignal().list.id || this.listSignal().list.accessToken;

    this.wishlistService
      .getListDetailsV2$(
        this.isPublicList(),
        listIdOrToken,
        Math.abs(LIST_PAGE_ITEMS * this.viewMoreProductsIteration),
        LIST_PAGE_ITEMS,
        this.isRecommendedList(),
      )
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map((listDetails: IOrchestratedListDetails) => {
          // favorites is empty. Throw error to not run Product Details APIs and trigger empty favorites.
          if (!listDetails.list.products.length) {
            throw new Error('EmptyList');
          }
          return listDetails;
        }),
        tap((listDetails: IOrchestratedListDetails) => {
          this.viewMoreProductsIteration++;
          this.currentProducts = this.currentProducts.concat(listDetails.list.products);
          this.setProductAttributes();
          this.totalCount = listDetails.list.productCount;
          this.listSignal.set(listDetails);
          this.showViewMore();
        }),
        mergeMap(() => {
          return this.getProductAvailability();
        }),
      )
      .subscribe({
        next: (availabilityResponse: ProductAvailabilityCollection) => {
          this.ecodeMessagingMap.set(availabilityResponse.ecodeMessagingMap);
        },
        error: (err: Error) => {
          if (err.message === 'EmptyList') {
            this.eventBusService.emit(new EmitEvent(Events.EmptyFavorites));
          }

          this.myAccountAnalyticsService.generateLogError(
            MyAccountErrorLogKeys.FAVORITES,
            `Error getting orchestrated list: ${listIdOrToken}`,
            err,
          );
        },
      });
  }

  private createProductDetailsObservable(
    page: number,
  ): Observable<IOrchestratedListDetails> | null {
    const listIdOrToken = this.listSignal().list.id || this.listSignal().list.accessToken;

    if (!listIdOrToken) return of(null);

    return this.wishlistService
      .getOrchestratedListDetails$(
        this.isPublicList(),
        listIdOrToken,
        Math.abs(PAGE_SIZE * (page - 1)),
        PAGE_SIZE,
      )
      .pipe(
        map((listProducts: IOrchestratedListDetails) => {
          if (!listProducts.list.products.length) {
            throw new Error('EmptyList');
          }

          return listProducts;
        }),
        tap((listProducts: IOrchestratedListDetails) => {
          this.updateComponentState(listProducts);
        }),
        mergeMap((listProducts: IOrchestratedListDetails) => {
          return this.getProductAvailability().pipe(
            tap((availabilityResponse: ProductAvailabilityCollection) => {
              this.ecodeMessagingMap.set(availabilityResponse.ecodeMessagingMap);
            }),
            catchError((error) => {
              return of(null);
            }),
            map(() => listProducts),
          );
        }),
        catchError((error) => {
          if (error.message === 'EmptyList') {
            this.eventBusService.emit(new EmitEvent(Events.EmptyFavorites));
          }

          this.myAccountAnalyticsService.generateLogError(
            MyAccountErrorLogKeys.FAVORITES,
            `Error getting orchestrated list: ${listIdOrToken}`,
            error,
          );
          return of(null);
        }),
      );
  }

  private updateComponentState(listProducts: IOrchestratedListDetails): void {
    this.listSignal.set(listProducts);
    this.currentProducts = listProducts.list.products;
    this.setProductAttributes();
    this.totalCount = listProducts.list.productCount;
  }
}
